import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { RelativeDatePlaceholdersEnum } from '../../../models/enums/relative-date-placeholders.enum';
import { ReportScheduleDialogParameterFieldTypeEnum } from '../../../models/enums/report-schedule-dialog-parameter-field-type.enum';
import { ReportScheduleDialogParameterFieldModel } from '../../../models/report-schedule-dialog-parameter-field.model';
import { ReportScheduleModel } from '../../../models/report-schedule.model';
import { ReportModel } from '../../../models/report.model';
import { SimpleItemModel } from '../../../models/simple-item.model';
import { ControllerService } from '../../../services/controller.service';
import { ReportSchedulesResource } from '../../../services/resources/report-schedules.resource';
import { ReportsResource } from '../../../services/resources/reports.resource';

@Component({
    selector: 'sl-report-schedule-dialog',
    templateUrl: './report-schedule-dialog.component.html',
    styleUrls: ['./report-schedule-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportScheduleDialogComponent extends BaseComponent implements OnInit {
    isLoading = true;
    isEditing = false;

    reportModel: ReportModel;
    reportScheduleModel: ReportScheduleModel;

    reportParameterFields: ReportScheduleDialogParameterFieldModel[];
    reportFormats: SimpleItemModel[];

    formGroup: FormGroup;

    private readonly dialogRef = inject(MatDialogRef);
    private readonly reportsResource = inject(ReportsResource);
    private readonly reportSchedulesResource = inject(ReportSchedulesResource);
    private readonly controllerService = inject(ControllerService);

    async ngOnInit(): Promise<void> {
        this.initForm();
        await this.loadData();
        this.isLoading = false;
        this.initParametersForm();
    }

    /**
     * Gets the possible export formats for this report
     * @returns A list with report formats to display
     */
    getReportFormats(): SimpleItemModel[] {
        return [
            { id: 'pdf', label: 'PDF' },
            { id: 'xls', label: 'Excel' },
            { id: 'doc', label: 'Word' },
            { id: 'csv', label: 'CSV' },
            { id: 'xml', label: 'XML' }
        ];
    }

    /**
     * Save the created / edited report schedule
     */
    save(): void {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAllAsTouched();
        if (!this.formGroup.valid) {
            return;
        }

        const model = new ReportScheduleModel(this.formGroup.getRawValue());
        model.reportId = this.reportModel.id;
        model.reportParameters = JSON.stringify(this.formGroup.value.parameters);

        if (this.isEditing) {
            // update
            model.id = this.reportScheduleModel.id;
            model.userId = this.reportScheduleModel.userId;
            this.reportSchedulesResource.edit(model).subscribe(result => this.closeIfSuccessful(result.value.isSuccessful));
        } else {
            // create
            this.reportSchedulesResource
                .add({ ...model, id: 0 })
                .subscribe(result => this.closeIfSuccessful(result.value.isSuccessful, true));
        }
    }

    /**
     * Delete this report schedule
     */
    deleteReportSchedule(): void {
        this.isLoading = true;
        // close dialog and reload the page with 'this.dialogRef.close(true)'
        this.reportSchedulesResource.remove(this.reportScheduleModel.id).subscribe(() => this.dialogRef.close({ reloadPage: true }));
    }

    /**
     * Initialize the form
     */
    private initForm(): void {
        this.formGroup = new FormGroup({
            reportFormat: new FormControl(undefined, Validators.required),
            nextExecutionCronSchedule: new FormControl('', Validators.required),
            reportValidForDays: new FormControl(30, Validators.required),
            shouldReceiveEmailNotification: new FormControl(false, Validators.required),
            parameters: new FormGroup({})
        });
    }

    /**
     * Initialize the report parameters form
     */
    private initParametersForm(): void {
        const parametersGroup = this.formGroup.controls['parameters'] as FormGroup;
        const defaultValue: any = {};
        for (const parameterField of this.reportParameterFields) {
            parametersGroup.addControl(
                parameterField.name,
                new FormControl(undefined, parameterField.isNullable ? undefined : Validators.required)
            );
            // general default value
            if (!!parameterField.defaultValue) {
                defaultValue[parameterField.name] = parameterField.defaultValue;
            }
            // always use default values for date parameters
            if (
                parameterField.type === ReportScheduleDialogParameterFieldTypeEnum.DateParameter
                || parameterField.type === ReportScheduleDialogParameterFieldTypeEnum.DateTimeParameter
            ) {
                // use LastExecutionDate as default for parameters named '@VonDatum', else use ExecutionDate
                defaultValue[parameterField.name] =
                    parameterField.name === '@VonDatum'
                        ? RelativeDatePlaceholdersEnum.LastExecutionDate
                        : RelativeDatePlaceholdersEnum.ExecutionDate;
            }
        }

        if (!!this.reportScheduleModel) {
            const parameters = JSON.parse(this.reportScheduleModel.reportParameters);
            parametersGroup.patchValue(parameters);
        } else {
            parametersGroup.patchValue(defaultValue);
        }
    }

    /**
     * Loads the data needed for creating or editing a report schedule
     */
    private async loadData(): Promise<void> {
        const reportModel = this.controllerService.currentResponse?.value as ReportModel;
        this.reportModel = reportModel;

        if (!this.reportModel) {
            // must have a report model
            this.dialogRef.close();
            return;
        }

        this.reportScheduleModel = (await firstValueFrom(this.reportSchedulesResource.getByReportId(this.reportModel.id))).value;
        if (!!this.reportScheduleModel) {
            this.isEditing = true;
            this.formGroup.patchValue(this.reportScheduleModel);
        }

        this.reportFormats = this.getReportFormats();
        this.reportParameterFields = (await firstValueFrom(this.reportsResource.getParameterFieldsById(this.reportModel.id))).value;
    }

    /**
     * Closes the dialog if an action was executed successfully
     * @param isSuccessful whether action was executed successfully
     */
    private closeIfSuccessful(isSuccessful: boolean, reloadPage = false): void {
        if (isSuccessful) {
            this.dialogRef.close({ reloadPage });
            // TODO refresh page (side reports menu & related action button)
        }
    }
}
